
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, Language } from './translations';
import { getUrlForId } from './routes';
import { useLocation } from 'react-router-dom';

import { urlMappings } from '../data/urlMappings';

type LanguageContextType = {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: string, params?: Record<string, any>) => string;
    getLink: (id: string, customSlug?: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [language, setLanguage] = useState<Language>('tr');
    const location = useLocation();

    useEffect(() => {
        // Detect language from URL path first
        const path = location.pathname;
        if (path.startsWith('/nl')) {
            setLanguage('nl');
        } else {
            setLanguage('tr');
        }
    }, [location.pathname]);

    const t = (key: string, params?: Record<string, any>): string => {
        const keys = key.split('.');
        let value: any = translations[language];

        for (const k of keys) {
            if (value && value[k]) {
                value = value[k];
            } else {
                // Fallback to TR if missing
                console.warn(`Translation missing for key: ${key} in ${language}`);
                return key;
            }
        }

        let result = value as string;
        if (params) {
            Object.entries(params).forEach(([k, v]) => {
                result = result.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
            });
        }
        return result;
    };

    const getLink = (id: string, customSlug?: string): string => {
        const baseUrl = getUrlForId(id, language) || urlMappings[id as keyof typeof urlMappings] || `/${id}`;
        if (customSlug) {
            return `${baseUrl}/${customSlug}`;
        }
        return baseUrl;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, getLink }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
